import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Package, Star, Gift, Calendar, X,
  ShoppingBag, ArrowRight, AlertCircle, Zap
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Catégories → libellé + couleur (marque Show Événement)
const CAT_CFG = {
  jeux:     { label: 'Jeux',     color: '#5DBCBF' },
  gourmand: { label: 'Gourmand', color: '#C7A24C' },
  buvette:  { label: 'Buvette',  color: '#1F9D7A' },
  photo:    { label: 'Photo',    color: '#7C5CBF' },
  son:      { label: 'Son',      color: '#E0697A' },
  deco:     { label: 'Déco',     color: '#D98E5A' },
  confort:  { label: 'Confort',  color: '#5A8FD9' },
  services: { label: 'Services', color: '#9CA3AF' },
};
const FAM_CAT = {
  jeux_bois: 'jeux', gonflable: 'jeux', minigolf: 'jeux',
  machine_gourmande: 'gourmand', brasero: 'gourmand', remorque: 'gourmand', foodtruck_presta: 'gourmand',
  tireuse: 'buvette', fut: 'buvette', glacons: 'buvette',
  photobooth: 'photo', livre_or: 'photo',
  son: 'son', video: 'son',
  arche: 'deco', deco_autre: 'deco',
  structure: 'confort', mobilier: 'confort', service: 'services',
};

// Libellés courts pour les vignettes "au choix"
const FAMILLE_SHORT = {
  jeux_bois: 'Jeu en bois', gonflable: 'Gonflable', minigolf: 'Mini-golf',
  machine_gourmande: 'Machine', brasero: 'Brasero', remorque: 'Remorque', foodtruck_presta: 'Food truck',
  tireuse: 'Tireuse', fut: 'Fût', glacons: 'Glaçons', photobooth: 'Photobooth', livre_or: "Livre d'or",
  arche: 'Arche', deco_autre: 'Déco', son: 'Sono', video: 'Vidéo', structure: 'Structure',
  mobilier: 'Mobilier', service: 'Service',
};

// Vignette produit (vraie image de la référence)
function ProductThumb({ g, color }) {
  return (
    <div style={{ width: 60 }} title={g.name}>
      <div style={{
        position: 'relative', width: 60, height: 60, borderRadius: 12, overflow: 'hidden',
        background: 'var(--teal-mist)', border: '1px solid var(--border)',
      }}>
        {g.image ? (
          <img src={g.image} alt={g.name} loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Package size={22} style={{ color, opacity: 0.4 }} />
          </div>
        )}
        {g.qty > 1 && (
          <span style={{
            position: 'absolute', top: 3, right: 3, fontSize: 10, fontWeight: 800, lineHeight: 1,
            padding: '2px 5px', borderRadius: 7, background: 'rgba(32,39,42,0.85)', color: '#fff',
          }}>×{g.qty}</span>
        )}
        {g.au_choix && (
          <span style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: 8.5, fontWeight: 800,
            textAlign: 'center', padding: '2px 0', background: 'rgba(197,162,76,0.95)', color: '#3a2c00',
            letterSpacing: '.02em',
          }}>AU CHOIX</span>
        )}
      </div>
      <p style={{
        fontSize: 10, textAlign: 'center', marginTop: 3, lineHeight: 1.15, color: 'var(--text-muted)',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {g.shortName}{g.sur_devis ? ' (sur devis)' : ''}
      </p>
    </div>
  );
}

function itemInfo(item) {
  const cat = item.au_choix
    ? (FAM_CAT[item.famille] || 'services')
    : (item.product?.categorie || FAM_CAT[item.product?.famille] || 'services');
  const name = item.au_choix ? item.label : (item.product?.nom || 'Produit');
  const image = item.au_choix ? item.image : (item.product?.images?.[0] || item.image || null);
  const shortName = item.au_choix
    ? (FAMILLE_SHORT[item.famille] || item.label)
    : (item.product?.nom || 'Produit');
  return { cat, name, shortName, image, qty: item.quantite, sur_devis: item.sur_devis, au_choix: !!item.au_choix };
}

function PackCard({ pack, onRequest }) {
  const [expanded, setExpanded] = useState(false);

  const available = pack.dispo_stock !== false;
  const barre = pack.prix_barre ?? pack.prix_affiche ?? 0;
  const prixPack = pack.prix_pack ?? pack.prix_final ?? 0;
  const supplement = pack.has_photobooth ? (pack.supplement_impressions || 0) : 0;

  // Regroupement des articles par catégorie
  const groups = {};
  (pack.items_detail || []).forEach(it => {
    const info = itemInfo(it);
    (groups[info.cat] = groups[info.cat] || []).push(info);
  });
  const orderedCats = Object.keys(groups).sort(
    (a, b) => Object.keys(CAT_CFG).indexOf(a) - Object.keys(CAT_CFG).indexOf(b)
  );

  return (
    <div className="rounded-2xl overflow-hidden flex flex-col transition-all"
      style={{
        background: '#FFFFFF', border: '1px solid var(--border)',
        opacity: available ? 1 : 0.72,
        filter: available ? 'none' : 'grayscale(0.55)',
        boxShadow: available ? undefined : 'none',
      }}
      data-testid={`pack-card-${pack.id}`}>
      {/* Image */}
      <div className="relative">
        {pack.image ? (
          <img src={pack.image} alt={pack.nom} className="w-full h-52 object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-52 flex items-center justify-center" style={{ background: 'var(--teal-mist)' }}>
            <Package size={48} style={{ color: 'var(--teal)', opacity: 0.3 }} />
          </div>
        )}
        {pack.occasion && (
          <span className="absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full"
            style={{ background: 'rgba(255,255,255,0.92)', color: 'var(--ink)' }}>
            {pack.occasion}
          </span>
        )}
        {pack.featured && (
          <span className="absolute top-3 right-3 flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full"
            style={{ background: 'rgba(212,175,55,0.95)', color: '#5C4500' }}>
            <Star size={10} fill="currentColor" /> Best-seller
          </span>
        )}
        {!available && (
          <div className="absolute inset-0 flex items-center justify-center"
            style={{ background: 'rgba(32,39,42,0.45)' }}>
            <span className="text-sm font-bold px-4 py-2 rounded-full"
              style={{ background: '#E0697A', color: '#fff' }} data-testid={`pack-indispo-${pack.id}`}>
              Indisponible
            </span>
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display font-bold text-lg mb-1" style={{ color: 'var(--ink)', letterSpacing: '-0.01em' }}>
          {pack.nom}
        </h3>
        {(pack.slogan || pack.description) && (
          <p className="text-sm mb-4 italic" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
            « {pack.slogan || pack.description} »
          </p>
        )}

        {/* Bandeau indisponibilité : produits manquants */}
        {!available && (pack.manquants || []).length > 0 && (
          <div className="flex items-start gap-2 p-2.5 rounded-xl mb-4"
            style={{ background: 'rgba(224,105,122,0.09)', border: '1px solid rgba(224,105,122,0.25)' }}>
            <AlertCircle size={14} style={{ color: '#E0697A', flexShrink: 0, marginTop: 1 }} />
            <p className="text-xs font-semibold" style={{ color: '#E0697A' }}>
              Indisponible — il manque : {pack.manquants.join(', ')}
            </p>
          </div>
        )}

        {/* Produits inclus, groupés par catégorie — vignettes réelles */}
        <div className="space-y-3 mb-4">
          {(expanded ? orderedCats : orderedCats.slice(0, 3)).map(cat => {
            const cfg = CAT_CFG[cat] || CAT_CFG.services;
            const list = groups[cat];
            const shown = expanded ? list : list.slice(0, 6);
            const rest = list.length - shown.length;
            return (
              <div key={cat}>
                <span className="text-xs font-bold px-2 py-0.5 rounded-md inline-block mb-2"
                  style={{ background: `${cfg.color}16`, color: cfg.color }}>{cfg.label}</span>
                <div className="flex flex-wrap gap-2">
                  {shown.map((g, i) => <ProductThumb key={i} g={g} color={cfg.color} />)}
                  {rest > 0 && (
                    <div style={{ width: 60, height: 60, borderRadius: 12, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', background: 'var(--teal-mist)', border: '1px dashed var(--border)',
                      fontSize: 13, fontWeight: 700, color: cfg.color }}>+{rest}</div>
                  )}
                </div>
              </div>
            );
          })}
          {orderedCats.length > 3 && (
            <button onClick={() => setExpanded(e => !e)}
              className="text-xs font-semibold transition-colors hover:opacity-70"
              style={{ color: 'var(--teal)' }}>
              {expanded ? 'Réduire' : `Voir tout (${orderedCats.length} catégories)`}
            </button>
          )}
        </div>

        {/* Livraison & installation incluses */}
        <div className="flex items-center gap-2 p-2.5 rounded-xl mb-4"
          style={{ background: 'rgba(31,157,122,0.07)', border: '1px solid rgba(31,157,122,0.15)' }}>
          <Gift size={14} style={{ color: '#1F9D7A', flexShrink: 0 }} />
          <p className="text-xs font-semibold" style={{ color: '#1F9D7A' }}>
            {pack.contenu_offert || 'Livraison & installation incluses'}
          </p>
        </div>

        <div className="flex-1" />

        {/* Prix : barré → pack → économie */}
        <div className="mt-auto pt-4" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="flex items-end justify-between mb-1">
            <div>
              {barre > prixPack && (
                <p className="text-sm line-through" style={{ color: '#E0697A', opacity: 0.8 }}>{barre.toFixed(0)}€</p>
              )}
              <p className="text-3xl font-bold price-gold" style={{ color: '#C7A24C' }}
                data-testid={`pack-price-${pack.id}`}>{prixPack.toFixed(0)}€</p>
            </div>
            {pack.economie > 0 && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full mb-1"
                style={{ background: 'rgba(31,157,122,0.12)', color: '#1F9D7A' }}>
                Vous économisez {pack.economie.toFixed(0)}€
              </span>
            )}
          </div>
          {supplement > 0 && (
            <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
              + supplément impressions ({supplement.toFixed(0)}€)
            </p>
          )}
          {supplement === 0 && <div className="mb-3" />}

          <Button
            onClick={() => available && onRequest(pack)}
            disabled={!available}
            className="w-full font-bold"
            style={{
              background: available ? 'var(--teal)' : '#D1D5DB',
              color: available ? '#fff' : '#6B7280',
              cursor: available ? 'pointer' : 'not-allowed',
            }}
            data-testid={`pack-request-${pack.id}`}>
            {available
              ? <><ShoppingBag size={16} className="mr-2" /> Demander ce pack</>
              : <><X size={16} className="mr-2" /> Indisponible</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Modal de demande de pack (public) — crée un devis au tarif préférentiel
function PackRequestModal({ pack, onClose }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ prenom: '', nom: '', email: '', telephone: '', date_evenement: '', adresse: '' });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const valid = f.prenom.trim() && f.nom.trim() && /\S+@\S+\.\S+/.test(f.email) && f.telephone.trim();
  const prixPack = pack.prix_pack ?? pack.prix_final ?? 0;

  async function submit() {
    if (!valid) { toast.error('Merci de renseigner prénom, nom, email et téléphone.'); return; }
    setSubmitting(true);
    try {
      const contenu = (pack.items_detail || []).filter((i) => i.inclus).map((i) =>
        i.au_choix ? `• ${i.label}` : `• ${i.product?.nom || ''}${(i.quantite || 1) > 1 ? ` ×${i.quantite}` : ''}`
      );
      const notes = `Demande du pack « ${pack.nom} » — tarif pack ${prixPack}€ (livraison en sus).\nContenu souhaité :\n${contenu.join('\n')}`;
      const res = await fetch(`${BACKEND}/api/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session: {
            type_evenement: null,
            date_evenement: f.date_evenement.trim() || null,
            adresse: f.adresse.trim() || null,
            nb_adultes: 0, nb_enfants: 0, produits: [],
            client_profile: 'particulier', notes,
          },
          client_nom: f.nom.trim(), client_prenom: f.prenom.trim(),
          client_email: f.email.trim(), client_telephone: f.telephone.trim(),
          client_profile: 'particulier', adresse_livraison: f.adresse.trim() || null,
          pack_id: pack.id,
        }),
      });
      if (!res.ok) throw new Error('create failed');
      const quote = await res.json();
      toast.success('Votre demande de pack est prête !');
      navigate(`/devis/${quote.id}`);
    } catch {
      toast.error('Impossible de créer la demande. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--border)', fontSize: 14, background: '#fff', color: 'var(--ink)' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(20,25,28,0.55)' }} data-testid="pack-request-modal" onClick={onClose}>
      <div className="rounded-2xl w-full max-w-lg overflow-hidden" style={{ background: '#fff', maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5" style={{ borderBottom: '1px solid var(--border)' }}>
          <div>
            <h3 className="font-display font-bold text-lg" style={{ color: 'var(--ink)' }}>Demander « {pack.nom} »</h3>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Tarif pack <span style={{ color: '#C7A24C', fontWeight: 700 }}>{prixPack.toFixed(0)}€</span> + livraison selon distance.
            </p>
          </div>
          <button onClick={onClose} data-testid="preq-close" className="p-1 rounded-lg" style={{ color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label style={labelStyle}>Prénom *</label>
              <input data-testid="preq-prenom" value={f.prenom} onChange={set('prenom')} style={inputStyle} placeholder="Marie" />
            </div>
            <div>
              <label style={labelStyle}>Nom *</label>
              <input data-testid="preq-nom" value={f.nom} onChange={set('nom')} style={inputStyle} placeholder="Dupont" />
            </div>
          </div>
          <div className="mb-3">
            <label style={labelStyle}>Email *</label>
            <input data-testid="preq-email" value={f.email} onChange={set('email')} style={inputStyle} placeholder="marie@email.com" type="email" />
          </div>
          <div className="mb-3">
            <label style={labelStyle}>Téléphone *</label>
            <input data-testid="preq-tel" value={f.telephone} onChange={set('telephone')} style={inputStyle} placeholder="06 12 34 56 78" />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-1">
            <div>
              <label style={labelStyle}>Date de l&apos;événement</label>
              <input data-testid="preq-date" value={f.date_evenement} onChange={set('date_evenement')} style={inputStyle} placeholder="2026-08-15" />
            </div>
            <div>
              <label style={labelStyle}>Lieu / adresse</label>
              <input data-testid="preq-adresse" value={f.adresse} onChange={set('adresse')} style={inputStyle} placeholder="Salle des fêtes, Figeac" />
            </div>
          </div>
        </div>

        <div className="p-5" style={{ borderTop: '1px solid var(--border)' }}>
          <Button onClick={submit} disabled={!valid || submitting} data-testid="preq-submit"
            className="w-full font-bold" style={{ background: 'var(--teal)', color: '#fff' }}>
            {submitting ? 'Envoi…' : 'Envoyer ma demande de pack'}
          </Button>
          <p className="text-xs text-center mt-2" style={{ color: 'var(--text-muted)' }}>
            Sans engagement — l&apos;équipe confirme les disponibilités et la livraison.
          </p>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
export default function PacksPageV2() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqPack, setReqPack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/packs-custom?disponible_only=true`);
      const data = await res.json();
      setPacks(data.packs || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function requestPack(pack) {
    setReqPack(pack);
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2.5rem', paddingBottom: '6rem' }}>
        {/* Header */}
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
            style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
            <Zap size={12} /> Packs événementiels
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3"
            style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
            Réalisez votre pack
          </h1>
          <p className="text-base max-w-2xl" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Nos packs combinent matériel de location et articles de boutique pour un événement clé en main.
            Chaque pack est vérifiable en disponibilité et personnalisable sur demande.
          </p>
        </div>

        {/* Info disponibilité */}
        <div className="flex items-center gap-3 p-4 rounded-2xl mb-8"
          style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
          <Calendar size={18} style={{ color: 'var(--teal)' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Disponibilité en temps réel</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Un pack n&apos;est proposable que si tous ses articles sont disponibles. Les packs grisés
              indiquent le ou les articles manquants — ils se réactivent automatiquement dès leur retour.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                <Skeleton className="w-full h-52" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : packs.length === 0 ? (
          <div className="text-center py-24">
            <Package size={56} className="mx-auto mb-5" style={{ color: 'var(--border)', opacity: 0.5 }} />
            <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--ink)' }}>Packs bientôt disponibles</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
              Nos packs thématiques sont en cours de préparation. En attendant, créez votre événement sur mesure !
            </p>
            <Link to="/creer-evenement"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold"
              style={{ background: 'var(--teal)', color: '#fff' }}>
              Créer mon événement <ArrowRight size={16}/>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {packs.map(pack => (
              <PackCard key={pack.id} pack={pack} onRequest={requestPack} />
            ))}
          </div>
        )}

        {/* CTA bas de page */}
        <div className="mt-16 text-center p-8 rounded-3xl" style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
          <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--ink)' }}>
            Vous avez une idée précise ?
          </h2>
          <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
            Utilisez notre configurateur pour créer votre événement entièrement sur mesure.
          </p>
          <Link to="/creer-evenement"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold"
            style={{ background: 'var(--teal)', color: '#fff' }}>
            Configurer mon événement <ArrowRight size={16}/>
          </Link>
        </div>
      </div>

      {reqPack && <PackRequestModal pack={reqPack} onClose={() => setReqPack(null)} />}

      <MobileStickyBar />
      <Footer />
    </div>
  );
}
